// src/routes/domains/agency/agency.routes.tsx
// All /agency/* routes wrapped in AgencyLayout via nested route pattern
// AgencyLayout uses <Outlet /> to render child pages
// routes/index.tsx renders parent route + children automatically

import React, { lazy } from 'react';
import { createProtectedRoute } from '../../core/createProtectedRoute';
import type { AppRoute } from '../../core/types';

// ── Layout ────────────────────────────────────────────────────────────────────
const AgencyLayout = lazy(() => import('@/components/layout/agency/AgencyLayout'));

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = lazy(() => import('@/pages/agency/Dashboard'));

// ── GroupOS / GDPR consent ────────────────────────────────────────────────────
const AgencyDashboard = lazy(() => import('@/pages/agency/AgencyDashboard'));
const AgencyUPRM = lazy(() => import('@/pages/agency/AgencyUPRM'));
const AgencyCohorts = lazy(() => import('@/pages/agency/AgencyCohorts'));
const AgencyCohortDetail = lazy(() => import('@/pages/agency/AgencyCohortDetail'));
const AgencyConsentAudit = lazy(() => import('@/pages/agency/AgencyConsentAudit'));
const AgencyConsentRequest = lazy(() => import('@/pages/agency/AgencyConsentRequest'));

// ── Core hub & welcome ────────────────────────────────────────────────────────
const AgencyHub = lazy(() => import('@/pages/agency/AgencyHub'));
const AgencyWelcomePage = lazy(() => import('@/pages/agency/AgencyWelcome').then(m => ({ default: m.AgencyWelcomePage ?? m.AgencyWelcome ?? m.default })));
const ToolDashboard = lazy(() => import('@/pages/agency/ToolDashboard'));
const BillingHub = lazy(() => import('@/pages/agency/BillingHub').then(m => ({ default: m.BillingHub ?? m.default })));

// ── Clients ───────────────────────────────────────────────────────────────────
const ClientsList = lazy(() => import('@/pages/agency/ClientsList.fixed'));
const ClientCreation = lazy(() => import('@/pages/agency/ClientCreation.fixed'));
const ClientIntelligence = lazy(() => import('@/pages/agency/ClientIntelligence'));
const ClientJourneyOrchestrator = lazy(() => import('@/pages/agency/ClientJourneyOrchestrator'));

// ── Cases ─────────────────────────────────────────────────────────────────────
const CaseCreation = lazy(() => import('@/pages/agency/CaseCreation').then(m => ({ default: m.CaseCreation ?? m.default })));
const CaseDetail = lazy(() => import('@/pages/agency/CaseDetail'));
const ClientDetail = lazy(() => import('@/pages/agency/ClientDetail'));
const CasePipelinePage = lazy(() => import('@/pages/agency/CasePipeline'));
const CaseAssignments = lazy(() => import('@/pages/agency/CaseAssignments'));
const KanbanBoard = lazy(() => import('@/pages/agency/KanbanBoard').then(m => ({ default: m.KanbanBoard ?? m.default })));

// ── Search ────────────────────────────────────────────────────────────────────
const GlobalSearchPage = lazy(() => import('@/pages/agency/GlobalSearch'));
const GlobalCaseSearch = lazy(() => import('@/pages/agency/GlobalCaseSearch'));

// ── Team & workflows ──────────────────────────────────────────────────────────
const TeamManagement = lazy(() => import('@/pages/agency/TeamManagement').then(m => ({ default: m.TeamManagement ?? m.default })));
const TeamAnalytics = lazy(() => import('@/pages/agency/TeamAnalytics'));
const TeamPerformance = lazy(() => import('@/pages/agency/TeamPerformance'));
const WorkflowAutomation = lazy(() => import('@/pages/agency/WorkflowAutomation.fixed').then(m => ({ default: m.WorkflowAutomation ?? m.default })));
const WorkflowDetail = lazy(() => import('@/pages/agency/WorkflowDetail'));
const WorkflowCenter = lazy(() => import('@/pages/agency/WorkflowCenter'));

// ── Documents & journeys ──────────────────────────────────────────────────────
const DocumentIntelligenceHub = lazy(() => import('@/pages/agency/DocumentIntelligenceHub').then(m => ({ default: m.DocumentIntelligenceHub })));
const DocumentPacks = lazy(() => import('@/pages/agency/DocumentPacks').then(m => ({ default: m.DocumentPacks })));
const AgencyJourneyMapPad = lazy(() => import('@/pages/agency/AgencyJourneyMapPad').then(m => ({ default: m.AgencyJourneyMapPad ?? m.default })));
const JourneyMap = lazy(() => import('@/pages/agency/JourneyMap').then(m => ({ default: m.JourneyMap ?? m.default })));
const CollaborationHub = lazy(() => import('@/pages/agency/CollaborationHub').then(m => ({ default: m.CollaborationHub ?? m.default })));

// ── Intelligence ──────────────────────────────────────────────────────────────
const CommandCenter = lazy(() => import('@/pages/agency/CommandCenter'));
const AIOptimization = lazy(() => import('@/pages/agency/AIOptimization'));
const BusinessIntelligence = lazy(() => import('@/pages/agency/BusinessIntelligence'));
const RevenueIntelligence = lazy(() => import('@/pages/agency/RevenueIntelligence'));

// ── Messaging ─────────────────────────────────────────────────────────────────
const AgencyMessages = lazy(() => import('@/pages/messaging/AgencyMessages'));
const CaseMessageThread = lazy(() => import('@/pages/messaging/CaseMessageThread'));

// ─── Meta helper ─────────────────────────────────────────────────────────────
const agency = (title: string, tags: string[] = [], intent: AppRoute['meta']['intent'] = 'dashboard') => ({
  tier: 'agency' as const,
  meta: { title, region: 'Global' as const, intent, aiTags: tags },
});

// ─── Child routes (render inside AgencyLayout <Outlet />) ────────────────────
const agencyChildren: AppRoute[] = [
  // Dashboard — index route (renders at /agency exactly)
  {
    id: 'agency.dashboard',
    domain: 'agency',
    path: '',
    index: true,
    element: createProtectedRoute(<Dashboard />, agency('Agency Dashboard', ['crm', 'dashboard'])),
  },

  // GroupOS / GDPR consent
  { id: 'agency.dashboard.groupos', domain: 'agency', path: 'dashboard', element: createProtectedRoute(<AgencyDashboard />, agency('Agency Dashboard — GroupOS', ['groupos', 'consent'])) },
  { id: 'agency.uprm', domain: 'agency', path: 'uprm/:clientId', element: createProtectedRoute(<AgencyUPRM />, agency('UPRM — Dual Rail', ['groupos', 'consent'])) },
  { id: 'agency.cohorts', domain: 'agency', path: 'cohorts', element: createProtectedRoute(<AgencyCohorts />, agency('Cohorts', ['groupos'])) },
  { id: 'agency.cohort_detail', domain: 'agency', path: 'cohorts/:cohortId', element: createProtectedRoute(<AgencyCohortDetail />, agency('Cohort Detail', ['groupos'])) },
  { id: 'agency.consents', domain: 'agency', path: 'consents', element: createProtectedRoute(<AgencyConsentAudit />, agency('Consent Audit Log', ['groupos', 'consent', 'gdpr'])) },
  { id: 'agency.consents.request', domain: 'agency', path: 'consents/request', element: createProtectedRoute(<AgencyConsentRequest />, agency('Request Consent', ['groupos', 'consent', 'gdpr'], 'form')) },

  // Legacy hub
  { id: 'agency.hub', domain: 'agency', path: 'hub', element: createProtectedRoute(<AgencyHub />, agency('Agency Hub')) },

  // Welcome & tools
  { id: 'agency.welcome', domain: 'agency', path: 'welcome', element: createProtectedRoute(<AgencyWelcomePage />, agency('Agency Welcome')) },
  { id: 'agency.billing_hub', domain: 'agency', path: 'billing-hub', element: createProtectedRoute(<BillingHub />, agency('Billing Hub', ['billing'])) },

  // Clients
  { id: 'agency.clients', domain: 'agency', path: 'clients', element: createProtectedRoute(<ClientsList />, agency('Clients', ['clients'])) },
  { id: 'agency.client_new', domain: 'agency', path: 'clients/new', element: createProtectedRoute(<ClientCreation />, agency('New Client', ['client'], 'form')) },
  { id: 'agency.client_detail', domain: 'agency', path: 'clients/:id', element: createProtectedRoute(<ClientDetail />, agency('Client Detail', ['client'])) },
  { id: 'agency.client_intelligence', domain: 'agency', path: 'client-intelligence', element: createProtectedRoute(<ClientIntelligence />, agency('Client Intelligence', ['client', 'ai'])) },
  { id: 'agency.client_journeys', domain: 'agency', path: 'client-journeys', element: createProtectedRoute(<ClientJourneyOrchestrator />, agency('Client Journeys', ['journey'])) },

  // Cases
  { id: 'agency.case_new', domain: 'agency', path: 'cases/new', element: createProtectedRoute(<CaseCreation />, agency('New Case', ['case'], 'form')) },
  { id: 'agency.case_detail', domain: 'agency', path: 'cases/:id', element: createProtectedRoute(<CaseDetail />, agency('Case Detail', ['case'])) },
  { id: 'agency.pipeline', domain: 'agency', path: 'pipeline', element: createProtectedRoute(<CasePipelinePage />, agency('Pipeline', ['pipeline', 'kanban'])) },
  { id: 'agency.kanban', domain: 'agency', path: 'kanban', element: createProtectedRoute(<KanbanBoard />, agency('Kanban', ['kanban'])) },
  { id: 'agency.assignments', domain: 'agency', path: 'assignments', element: createProtectedRoute(<CaseAssignments />, agency('Assignments', ['assignments'])) },

  // Search
  { id: 'agency.global_search', domain: 'agency', path: 'global-search', element: createProtectedRoute(<GlobalSearchPage />, agency('Global Search', ['search'])) },

  // Team
  { id: 'agency.team', domain: 'agency', path: 'team', element: createProtectedRoute(<TeamManagement />, agency('Team', ['team'])) },
  { id: 'agency.team_analytics', domain: 'agency', path: 'team-analytics', element: createProtectedRoute(<TeamAnalytics />, agency('Team Analytics', ['team'])) },
  { id: 'agency.team_performance', domain: 'agency', path: 'team-performance', element: createProtectedRoute(<TeamPerformance />, agency('Team Performance', ['team'])) },

  // Workflows
  { id: 'agency.workflows', domain: 'agency', path: 'workflows', element: createProtectedRoute(<WorkflowAutomation />, agency('Workflows', ['workflow'])) },
  { id: 'agency.workflow_detail', domain: 'agency', path: 'workflows/:id', element: createProtectedRoute(<WorkflowDetail />, agency('Workflow Detail', ['workflow'])) },
  { id: 'agency.workflow_center', domain: 'agency', path: 'workflow-center', element: createProtectedRoute(<WorkflowCenter />, agency('Workflow Centre', ['workflow'])) },

  // Documents
  { id: 'agency.documents', domain: 'agency', path: 'documents', element: createProtectedRoute(<DocumentIntelligenceHub />, agency('Documents', ['documents'])) },
  { id: 'agency.documents_case', domain: 'agency', path: 'documents/:caseId', element: createProtectedRoute(<DocumentIntelligenceHub />, agency('Case Documents', ['documents'])) },
  { id: 'agency.document_packs', domain: 'agency', path: 'document-packs', element: createProtectedRoute(<DocumentPacks />, agency('Document Packs', ['documents'])) },
  { id: 'agency.journey_map_pad', domain: 'agency', path: 'journey-map-pad', element: createProtectedRoute(<AgencyJourneyMapPad />, agency('Journey Map Pad', ['journey'])) },
  { id: 'agency.journey_map', domain: 'agency', path: 'journey-map', element: createProtectedRoute(<JourneyMap />, agency('Journey Map', ['journey'])) },
  { id: 'agency.journey_map_id', domain: 'agency', path: 'journey-map/:id', element: createProtectedRoute(<JourneyMap />, agency('Journey Map', ['journey'])) },
  { id: 'agency.collaboration', domain: 'agency', path: 'collaboration', element: createProtectedRoute(<CollaborationHub />, agency('Collaboration', ['collaboration'])) },

  // Intelligence
  { id: 'agency.command_center', domain: 'agency', path: 'command-center', element: createProtectedRoute(<CommandCenter />, agency('Command Centre', ['command', 'ai'])) },
  { id: 'agency.ai_optimization', domain: 'agency', path: 'ai-optimization', element: createProtectedRoute(<AIOptimization />, agency('AI Optimization', ['ai'])) },
  { id: 'agency.business_intelligence', domain: 'agency', path: 'business-intelligence', element: createProtectedRoute(<BusinessIntelligence />, agency('Business Intelligence', ['bi'])) },
  { id: 'agency.revenue_intelligence', domain: 'agency', path: 'revenue-intelligence', element: createProtectedRoute(<RevenueIntelligence />, agency('Revenue Intelligence', ['revenue'])) },

  // Messages
  { id: 'agency.messages', domain: 'agency', path: 'messages', element: createProtectedRoute(<AgencyMessages />, agency('Messages', ['messages'])) },
  { id: 'agency.message_thread', domain: 'agency', path: 'messages/:caseId', element: createProtectedRoute(<CaseMessageThread />, agency('Message Thread', ['messages'])) },
];

// ─── Parent route — AgencyLayout wraps all children via <Outlet /> ────────────
export const agencyRoutes: AppRoute[] = [
  {
    id: 'agency.root',
    domain: 'agency',
    path: '/agency',
    // AgencyLayout renders the nav + <Outlet /> for all child pages
    element: createProtectedRoute(<AgencyLayout />, agency('Agency OS', ['agency'])),
    children: agencyChildren,
  },

  // Tools route lives outside /agency path but still uses agency layout context
  {
    id: 'agency.tools',
    domain: 'agency',
    path: '/tools',
    element: createProtectedRoute(<ToolDashboard />, agency('Tools', ['tools'])),
  },

  // Global case search — separate path
  {
    id: 'agency.global_case_search',
    domain: 'agency',
    path: '/global/case-search',
    element: createProtectedRoute(<GlobalCaseSearch />, agency('Global Case Search', ['search'])),
  },
];
